'use client';

import type { HeroSlide } from './homepage-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useCallback, useEffect, useRef, useState } from 'react';

const AUTOPLAY_MS = 6000;

export function HeroSlider({ slides, goToLabel }: { slides: HeroSlide[]; goToLabel: string }) {
  const [active, setActive] = useState(0);
  const [requested, setRequested] = useState(0);
  const [paused, setPaused] = useState(false);
  const loaded = useRef(new Set<number>());
  const showSlide = useCallback((index: number) => {
    setRequested(index);
    if (loaded.current.has(index)) setActive(index);
  }, []);

  useEffect(() => {
    if (
      paused ||
      slides.length < 2 ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }
    const timer = window.setTimeout(() => showSlide((active + 1) % slides.length), AUTOPLAY_MS);
    return () => window.clearTimeout(timer);
  }, [active, paused, slides.length, showSlide]);

  return (
    <div
      className="relative overflow-hidden bg-primary/5"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div
        className="flex transition-transform duration-700 [transition-timing-function:cubic-bezier(.7,0,.2,1)]"
        style={{ transform: `translateX(-${active * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <Link
            key={slide.id}
            href={slide.href}
            prefetch={false}
            className="aspect-[1440/400] flex-[0_0_100%] min-[641px]:aspect-[1440/300]"
            aria-hidden={index !== active}
            tabIndex={index === active ? undefined : -1}
          >
            {(index === 0 || index === active || index === requested) && (
              <Image
                src={slide.image.url}
                alt={slide.image.alt}
                width={1440}
                height={300}
                sizes="100vw"
                quality={75}
                priority={index === 0}
                fetchPriority={index === 0 ? 'high' : undefined}
                loading={index === requested ? 'eager' : undefined}
                onLoad={() => {
                  loaded.current.add(index);
                  if (index === requested) setActive(index);
                }}
                className="block aspect-[1440/400] w-full object-cover object-left min-[641px]:aspect-[1440/300] min-[641px]:object-center"
              />
            )}
          </Link>
        ))}
      </div>
      {slides.length > 1 && (
        <div className="absolute right-5 top-4 flex gap-1.5">
          {slides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`${goToLabel} ${index + 1}`}
              aria-current={index === active}
              onClick={() => showSlide(index)}
              className={cn(
                'flex h-11 w-11 cursor-pointer items-center justify-center rounded-full p-0',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'block h-2 w-2 rounded-full bg-white/60',
                  index === active && 'w-[26px] bg-white',
                )}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
